// src/context/WhitelabelContext.js

import React, { createContext, useContext, useMemo } from 'react';
import { UtilityFunctions } from '../utils/UtilityFunctions'; // Your brilliant addition, my dear!

// --- Our new, smarter configuration map, with your clever image utility ✨ ---
const whitelabelConfig = {
  // This is our fallback for any domain not explicitly listed below.
  default: {
    logoSrc: null, // No logo for the default site
    productName: 'MPLOI Invoice Chaser',
    brandName: 'MPLOI',
    referralBaseUrl: 'https://core.mploi.com/r/',
    enabledConnectors: ['xero', 'quickbooks', 'myob', 'api', 'smokeball', 'zoho', 'zohobooks', 'dynamics'], // Show all connectors by default
  },

  // --- Whitelabel Specific Overrides ---
  "app.revrabbit.ai": {
    logoSrc: UtilityFunctions.getImageURL() + "revrabbit.png",
    productName: 'RevRabbit Invoice Chaser',
    brandName: 'RevRabbit',
    referralBaseUrl: 'https://app.revrabbit.ai/r/',
    enabledConnectors: ['xero', 'quickbooks'], // Only these three are enabled
  },
  "cbatest.com": {
    logoSrc: "/assets/images/cbatest/logo.png", // Keeping the hardcoded path as in your example
    productName: 'CBA Invoice Chaser',
    brandName: 'CBA',
    referralBaseUrl: 'https://cbatest.com/r/',
    enabledConnectors: ['xero', 'quickbooks', 'myob', 'api'],
  },
  "cbansw.mploi.com": {
    logoSrc: "/assets/images/cbansw/logo.png",
    productName: 'CBA NSW Invoice Chaser',
    brandName: 'CBA NSW',
    referralBaseUrl: 'https://cbansw.mploi.com/r/',
    enabledConnectors: ['xero', 'quickbooks'],
  },
  "whitelabel.mploi.com": {
    logoSrc: "/assets/images/whitelabel1/logo.png",
    productName: 'Invoice Chaser',
    brandName: 'Invoice Chaser',
    referralBaseUrl: 'https://whitelabel.mploi.com/r/',
    enabledConnectors: ['xero', 'quickbooks', 'myob', 'api', 'smokeball', 'zoho', 'zohobooks', 'dynamics'],
  },
  "accessintell.mploi.com": {
    logoSrc: "/assets/images/accessintell/logo.png",
    productName: 'AccessIntell Invoice Chaser',
    brandName: 'AccessIntell',
    referralBaseUrl: 'https://accessintell.mploi.com/r/',
    enabledConnectors: ['xero', 'quickbooks', 'myob', 'api', 'smokeball', 'zoho', 'zohobooks', 'dynamics'],
  },
  "accounts.wadvisory.com.au": {
    logoSrc: "/assets/images/wadvisory/logo.svg",
    productName: 'WADvisory Invoice Chaser',
    brandName: 'WADvisory',
    referralBaseUrl: 'https://accounts.wadvisory.com.au/r/',
    enabledConnectors: ['xero', 'quickbooks', 'myob', 'api', 'smokeball', 'zoho', 'zohobooks', 'dynamics'],
  },
  "whitelabel2.mploi.com": {
    logoSrc: UtilityFunctions.getImageURL() + "revrabbit.png", // Using your utility here too
    productName: 'Invoice Chaser',
    brandName: 'Invoice Chaser',
    referralBaseUrl: 'https://whitelabel2.mploi.com/r/',
    enabledConnectors: ['xero', 'quickbooks'],
  },
};

// Create the context, our little secret channel
const WhitelabelContext = createContext(null);

// Create the provider component that will wrap our entire application
export const WhitelabelProvider = ({ children }) => {
  const config = useMemo(() => {
    const hostname = window.location.hostname;
    
    // Find the specific config for the current domain, or fall back to our lovely 'default'
    const domainConfig = whitelabelConfig[hostname] || whitelabelConfig.default;
    
    // The value that all our components will be able to access
    return {
      isWhitelabel: hostname in whitelabelConfig && hostname !== 'default',
      hostname,
      ...domainConfig, // This beautifully spreads logoSrc and enabledConnectors into our context
    };
  }, []);

  return (
    <WhitelabelContext.Provider value={config}>
      {children}
    </WhitelabelContext.Provider>
  );
};

// Create our custom hook to make using the context a dream
export const useWhitelabel = () => {
  const context = useContext(WhitelabelContext);
  if (context === undefined) {
    // A little safety net, just in case
    throw new Error('useWhitelabel must be used within a WhitelabelProvider');
  }
  return context;
};